import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@linktivity/link-ui';

import { RailwayTrainInfo } from '@link/china-railway/services/types';

import styles from './confirmBookingModal.module.css';

export interface ConfirmBookingModalProps {
  open: boolean;
  loading: boolean;
  isPreOrder?: boolean;
  onClose: () => void;
  onConfirm: () => void;
  train: RailwayTrainInfo | null;
  travelDate?: string;
  seatClass?: string;
  passengerName?: string;
  passengerCount?: number;
}

const ConfirmBookingModal: React.FC<ConfirmBookingModalProps> = ({
  open,
  loading,
  isPreOrder,
  onClose,
  onConfirm,
  train,
  travelDate,
  seatClass,
  passengerName,
  passengerCount
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  if (!open || !train) return null;

  const handleMaskClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // 格式化日期显示，例如 "2024-12-19" -> "12月19日"
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    const [, month, day] = dateStr.split('-');
    return t('confirmBooking.date', {
      month: parseInt(month),
      day: parseInt(day)
    });
  };

  return (
    <div className={styles.overlay} onClick={handleMaskClick}>
      <div className={styles.modal}>
        <h2 className={styles.title}>{t('confirmBooking.title')}</h2>
        <p
          className={
            isPreOrder ? styles.descriptionWarning : styles.description
          }
        >
          {isPreOrder
            ? t('confirmBooking.preOrderDescription')
            : t('confirmBooking.description')}
        </p>

        <div className={styles.infoCard}>
          <div className={styles.routeInfo}>
            {train.fromStationName}→{train.toStationName}
          </div>

          <div className={styles.infoItem}>
            <span className={styles.label}>
              {t('confirmBooking.departTime')}
              {t('common.colon')}
            </span>
            <span className={styles.value}>
              {formatDate(travelDate)} {train.departTimeStr}
            </span>
          </div>

          <div className={styles.infoItem}>
            <span className={styles.label}>
              {t('confirmBooking.trainNumber')}
              {t('common.colon')}
            </span>
            <span className={styles.value}>{train.trainNumber}</span>
          </div>

          <div className={styles.infoItem}>
            <span className={styles.label}>
              {t('confirmBooking.seatNumber')}
              {t('common.colon')}
            </span>
            <span className={styles.value}>
              {t('confirmBooking.autoAssign')}
            </span>
          </div>

          <div className={styles.infoItem}>
            <span className={styles.label}>
              {t('confirmBooking.passengerName')}
              {t('common.colon')}
            </span>
            <span className={styles.value}>{passengerName}</span>
          </div>

          <div className={styles.infoItem}>
            <span className={styles.label}>
              {t('confirmBooking.ticketCount')}
              {t('common.colon')}
            </span>
            <span className={styles.value}>
              {seatClass ? t(`seatClass.${seatClass}`) : ''}×{passengerCount}
            </span>
          </div>
        </div>

        <div className={styles.actions}>
          <Button
            variant="text"
            className={styles.cancelButton}
            onClick={onClose}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="text"
            className={styles.confirmButton}
            onClick={onConfirm}
            loading={loading}
          >
            {t('confirmBooking.confirmBook')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmBookingModal;
